<template>
  <div class="material-upload" :class="{ 'is-disabled': disabled }">
    <!-- 单图 -->
    <template v-if="accept === 'image' && !multiple">
      <div
        v-if="singleUrl"
        class="upload-single"
        :style="{ width: size + 'px', height: size + 'px' }"
      >
        <img :src="singleUrl" alt="" @click="openPreview(singleUrl)" />
        <div class="upload-single__mask">
          <i class="el-icon-zoom-in" title="预览" @click="openPreview(singleUrl)" />
          <i class="el-icon-delete" title="移除" @click="clear" />
        </div>
      </div>
      <div
        v-else
        class="upload-single upload-single--empty"
        :style="{ width: size + 'px', height: size + 'px' }"
        @click="openPicker"
      >
        <i class="el-icon-plus" />
      </div>
      <div class="upload-actions">
        <el-upload
          action="#"
          accept="image/jpeg,image/png,image/gif,image/webp"
          :show-file-list="false"
          :http-request="handleUpload"
          :disabled="disabled || uploading"
        >
          <el-button size="mini" type="text" :loading="uploading">上传</el-button>
        </el-upload>
        <el-button size="mini" type="text" :disabled="disabled" @click="openPicker">素材库</el-button>
      </div>
    </template>

    <!-- 多图 -->
    <template v-else-if="accept === 'image' && multiple">
      <div class="upload-multi">
        <div
          v-for="(url, index) in urls"
          :key="url + index"
          class="upload-multi__item"
          :style="{ width: size + 'px', height: size + 'px' }"
        >
          <img :src="url" alt="" @click="openPreview(url)" />
          <span class="upload-multi__remove" @click="removeAt(index)">
            <i class="el-icon-close" />
          </span>
        </div>
        <div
          v-if="urls.length < limit"
          class="upload-multi__add"
          :style="{ width: size + 'px', height: size + 'px' }"
          @click="openPicker"
        >
          <i class="el-icon-plus" />
          <span>{{ urls.length }}/{{ limit }}</span>
        </div>
      </div>
      <div class="upload-actions">
        <el-upload
          action="#"
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp"
          :show-file-list="false"
          :http-request="handleUpload"
          :disabled="disabled || uploading || urls.length >= limit"
        >
          <el-button size="mini" type="text" :loading="uploading">上传</el-button>
        </el-upload>
        <el-button size="mini" type="text" :disabled="disabled" @click="openPicker">素材库</el-button>
      </div>
    </template>

    <!-- 文件 -->
    <template v-else>
      <div v-if="singleUrl" class="upload-file">
        <i class="el-icon-document" />
        <a :href="singleUrl" target="_blank" rel="noopener" class="upload-file__name">
          {{ fileName }}
        </a>
        <i class="el-icon-delete upload-file__remove" title="移除" @click="clear" />
      </div>
      <div v-else class="upload-file upload-file--empty">未选择文件</div>
      <div class="upload-actions">
        <el-upload
          action="#"
          accept=".zip,.rar,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          :show-file-list="false"
          :http-request="handleUpload"
          :disabled="disabled || uploading"
        >
          <el-button size="mini" type="text" :loading="uploading">上传文件</el-button>
        </el-upload>
      </div>
    </template>

    <MaterialPicker
      :visible.sync="pickerVisible"
      :multiple="multiple"
      :value="value"
      @confirm="onPick"
    />

    <el-dialog
      title="预览"
      :visible="previewVisible"
      width="720px"
      append-to-body
      @close="previewVisible = false"
    >
      <img v-if="previewUrl" :src="previewUrl" class="preview-img" alt="" />
    </el-dialog>
  </div>
</template>

<script>
import { uploadAttachment } from '@/api/attachment'
import MaterialPicker from '@/components/MaterialLibrary/MaterialPicker.vue'

export default {
  name: 'MaterialUpload',
  components: { MaterialPicker },
  props: {
    // 单图/文件为字符串地址，多图为地址数组
    value: {
      type: [String, Array],
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: 'image'
    },
    limit: {
      type: Number,
      default: 9
    },
    size: {
      type: Number,
      default: 100
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploading: false,
      pickerVisible: false,
      previewVisible: false,
      previewUrl: ''
    }
  },
  computed: {
    urls() {
      if (Array.isArray(this.value)) {
        return this.value.filter(Boolean)
      }
      return this.value ? [this.value] : []
    },
    singleUrl() {
      return Array.isArray(this.value) ? this.value[0] || '' : this.value || ''
    },
    fileName() {
      if (!this.singleUrl) return ''
      const list = String(this.singleUrl).split('/')
      const name = list[list.length - 1] || ''
      try {
        return decodeURIComponent(name)
      } catch (e) {
        return name
      }
    }
  },
  methods: {
    emitValue(next) {
      if (this.multiple) {
        this.$emit('input', next)
      } else {
        this.$emit('input', next[0] || '')
      }
    },
    clear() {
      this.emitValue([])
    },
    removeAt(index) {
      const next = this.urls.slice()
      next.splice(index, 1)
      this.emitValue(next)
    },
    openPreview(url) {
      if (!url) return
      this.previewUrl = url
      this.previewVisible = true
    },
    openPicker() {
      if (this.disabled) return
      this.pickerVisible = true
    },
    onPick(picked) {
      const list = Array.isArray(picked) ? picked : [picked]
      if (!list.length) return
      if (!this.multiple) {
        this.emitValue([list[0]])
        return
      }
      const next = this.urls.slice()
      list.forEach((url) => {
        if (url && next.indexOf(url) === -1 && next.length < this.limit) {
          next.push(url)
        }
      })
      this.emitValue(next)
    },
    handleUpload(options) {
      const file = options.file
      if (!file) return
      if (this.multiple && this.urls.length >= this.limit) {
        this.$message.warning(`最多选择 ${this.limit} 张图片`)
        return
      }
      this.uploading = true
      const type = this.accept === 'file' ? 2 : 1
      uploadAttachment({ file, groupId: 0, type })
        .then((asset) => {
          const url = asset && asset.url ? asset.url : ''
          if (!url) return
          if (this.multiple) {
            const next = this.urls.slice()
            if (next.length < this.limit) next.push(url)
            this.emitValue(next)
          } else {
            this.emitValue([url])
          }
          this.$message.success('上传成功')
        })
        .catch(() => {})
        .finally(() => {
          this.uploading = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.material-upload {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.is-disabled {
    opacity: 0.7;
  }
}

.upload-single {
  position: relative;
  border: 1px dashed var(--app-border);
  border-radius: 10px;
  overflow: hidden;
  background: #f7faf8;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &--empty {
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 22px;
      color: #9bb3aa;
    }
  }

  &__mask {
    position: absolute;
    inset: 0;
    display: none;
    align-items: center;
    justify-content: center;
    gap: 14px;
    background: rgba(0, 0, 0, 0.45);

    i {
      color: #fff;
      font-size: 17px;
      cursor: pointer;

      &:hover {
        color: #d8f0e4;
      }
    }
  }

  &:hover &__mask {
    display: flex;
  }
}

.upload-multi {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  &__item {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--app-border);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      cursor: zoom-in;
    }
  }

  &__remove {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  &__add {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: 1px dashed var(--app-border);
    border-radius: 8px;
    color: #9bb3aa;
    cursor: pointer;
    background: #f7faf8;
    font-size: 12px;

    i {
      font-size: 20px;
    }

    &:hover {
      border-color: var(--app-primary-muted);
      color: var(--app-primary);
    }
  }
}

.upload-file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid var(--app-border);
  border-radius: 8px;
  background: #f7faf8;
  font-size: 13px;
  max-width: 460px;

  &--empty {
    color: var(--app-text-secondary);
  }

  &__name {
    flex: 1;
    color: var(--app-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: none;
  }

  &__remove {
    color: #c0666b;
    cursor: pointer;
  }
}

.upload-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.preview-img {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
}
</style>
